import { IdentifierFact } from './chunk-OAF4WNMT.js';
import { ProtocolIcon } from './chunk-G5NTCHJK.js';
import { ProtocolBadge } from './chunk-DCE7EPKQ.js';
import { WORMHOLE, WORMHOLE_COMPONENTS, WORMHOLE_VAA_KIND, WORMHOLE_SIGNED_QUOTE_KIND, WORMHOLE_EXECUTOR_REQUEST_KIND, WORMHOLE_RELAY_INSTRUCTIONS_KIND, WORMHOLE_TRANSFER_PAYLOAD_KIND, WORMHOLE_REQUEST_PREFIX_LABELS, WORMHOLE_EXECUTOR_FIELD_LABELS, SectionCard, FactGrid, pickField, pickFrom, WORMHOLE_VM_FIELD_LABELS, WORMHOLE_FIELD_LABELS, ProtocolValue, WORMHOLE_VAA_ID_FACT, ProtocolParamList, WORMHOLE_QUOTE_FIELD_LABELS } from './chunk-RYYWSL2Y.js';
import { Send, ShieldCheck, ListChecks, ScrollText, ArrowLeftRight, Radio, Check, X } from 'lucide-react';
import { MiddleEllipsis, CopyButton } from '@authe/ui-core';
import { jsxs, jsx } from 'react/jsx-runtime';
import { rawOf, displayOf, isEnriched, decodedOf, kindOf } from '@authe/fe-commons/storage';
import { isValidAddress } from '@authe/fe-commons/chains';

function isRecord(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function text(v) {
  if (v == null) return "";
  return typeof v === "bigint" ? v.toString() : String(v);
}
function WormholeExecutorRequest({
  decoded,
  display
}) {
  const d = isRecord(decoded) ? decoded : {};
  const prefix = text(d.prefix);
  const prefixLabel = WORMHOLE_REQUEST_PREFIX_LABELS[prefix];
  const rawBody = text(d.raw);
  const facts = [
    {
      label: WORMHOLE_EXECUTOR_FIELD_LABELS.prefix,
      value: /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-sm bg-primary/10 px-1.5 py-px text-2xs font-medium text-primary", children: prefix }),
        prefixLabel && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: prefixLabel })
      ] }),
      when: prefix
    },
    {
      label: WORMHOLE_EXECUTOR_FIELD_LABELS.autoDiscover,
      value: text(d.autoDiscover) === "1" ? "Yes" : text(d.autoDiscover),
      when: d.autoDiscover != null
    }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: WORMHOLE.protocolId, icon: Send }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: text(display) || "Executor request" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: WORMHOLE.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: WORMHOLE.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        rawBody && /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "Request body" }),
          /* @__PURE__ */ jsxs("div", { className: "pt-2 inline-flex min-w-0 items-center gap-1 align-middle", children: [
            /* @__PURE__ */ jsx(MiddleEllipsis, { value: rawBody, kind: "hash" }),
            /* @__PURE__ */ jsx(CopyButton, { value: rawBody })
          ] })
        ] })
      ]
    }
  );
}
function isRecord2(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function text2(v) {
  v = displayOf(v) ?? rawOf(v);
  if (v == null) return "";
  return typeof v === "bigint" ? v.toString() : String(v);
}
function WormholeActor({ value, chainId }) {
  const raw = rawOf(value);
  const chainValue = rawOf(chainId);
  const chain2 = typeof chainValue === "string" && chainValue ? chainValue : void 0;
  return typeof raw === "string" && chain2 && isValidAddress(raw, chain2) ? /* @__PURE__ */ jsx(ProtocolValue, { value: raw, type: "address", chain: chain2 }) : /* @__PURE__ */ jsx(ProtocolValue, { value });
}
function Hex({ value }) {
  const t = text2(value);
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 align-middle", children: [
    /* @__PURE__ */ jsx(MiddleEllipsis, { value: t, kind: "hash" }),
    /* @__PURE__ */ jsx(CopyButton, { value: t })
  ] });
}
function strip0x(hex) {
  return hex.startsWith("0x") || hex.startsWith("0X") ? hex.slice(2) : hex;
}
function hasHex(hex) {
  return strip0x(hex).length > 0;
}
function buildVaaId(chain2, emitter, sequence) {
  const c = text2(rawOf(chain2)).trim();
  const seq = text2(rawOf(sequence)).trim();
  const em = strip0x(text2(rawOf(emitter)).trim().toLowerCase());
  if (!/^\d+$/.test(c) || !/^\d+$/.test(seq) || !/^[0-9a-f]{1,64}$/.test(em)) return void 0;
  return `${c}/${em}/${seq}`;
}
function isDecodedStruct(value) {
  if (isRecord2(decodedOf(value))) return true;
  return (kindOf(value) ?? "").startsWith(`${WORMHOLE.protocolId}.`);
}
function ValidBadge({ valid }) {
  const cls = valid ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive";
  const Icon = valid ? Check : X;
  return /* @__PURE__ */ jsxs("span", { className: `inline-flex items-center gap-1 rounded-sm px-1.5 py-px text-2xs font-medium ${cls}`, children: [
    /* @__PURE__ */ jsx(Icon, { className: "size-3", "aria-hidden": true }),
    valid ? "Verified" : "Invalid"
  ] });
}
function VmSignatures({ signatures }) {
  return /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
    /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: `${WORMHOLE_VM_FIELD_LABELS.signatures} (${signatures.length})` }),
    /* @__PURE__ */ jsx("ul", { className: "mt-2 space-y-1", children: signatures.map((sig, i) => {
      const idxRaw = text2(rawOf(pickFrom(sig, "guardianIndex", "index")));
      const idx = idxRaw || String(i);
      const r = strip0x(text2(rawOf(pickFrom(sig, "r"))).toLowerCase());
      const s = strip0x(text2(rawOf(pickFrom(sig, "s"))).toLowerCase());
      const vNum = Number(text2(rawOf(pickFrom(sig, "v"))) || "0");
      const v = (Number.isFinite(vNum) ? vNum : 0).toString(16).padStart(2, "0");
      const hex = r || s ? `0x${r}${s}${v}` : "";
      return /* @__PURE__ */ jsxs("li", { className: "flex min-w-0 items-center gap-2 text-2xs", children: [
        /* @__PURE__ */ jsxs("span", { className: "w-8 shrink-0 text-right tabular-nums text-muted-foreground", children: [
          "#",
          idx
        ] }),
        hex ? /* @__PURE__ */ jsx(Hex, { value: hex }) : /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" })
      ] }, `${idx}-${i}`);
    }) })
  ] });
}
function WormholeParseVM({ call }) {
  const encodedVm = pickField(call.params, "encodedVM", "encodedVm", "encoded", "encodedVaa", "vaa");
  const vm = pickField(call.returnParams, "vm");
  const valid = call.returnParams.find((p) => p.name != null && /^valid$/i.test(p.name))?.value;
  const reason = call.returnParams.find((p) => p.name != null && /^reason$/i.test(p.name))?.value;
  const emitterChain = pickFrom(vm, "emitterChainId", "emitterChain");
  const emitterAddress = pickFrom(vm, "emitterAddress", "emitter");
  const sequence = pickFrom(vm, "sequence");
  const version = pickFrom(vm, "version");
  const timestamp = pickFrom(vm, "timestamp");
  const nonce = pickFrom(vm, "nonce");
  const consistency = pickFrom(vm, "consistencyLevel");
  const guardianSet = pickFrom(vm, "guardianSetIndex", "guardianSet");
  const payload = pickFrom(vm, "payload");
  const hash = pickFrom(vm, "hash");
  const signatures = vm != null ? pickFrom(vm, "signatures") : void 0;
  const sigList = Array.isArray(signatures) ? signatures : [];
  const vaaId = buildVaaId(emitterChain, emitterAddress, sequence);
  const validRaw = rawOf(valid);
  const encodedDecoded = encodedVm != null && isDecodedStruct(encodedVm);
  const encodedHex = text2(rawOf(encodedVm));
  const payloadDecoded = payload != null && isDecodedStruct(payload);
  const payloadHex = text2(rawOf(payload));
  const facts = [
    {
      label: "Contract",
      value: call.address != null ? /* @__PURE__ */ jsx(ProtocolValue, { value: call.address }) : void 0,
      when: call.address
    },
    {
      label: WORMHOLE_VM_FIELD_LABELS.valid,
      value: typeof validRaw === "boolean" ? /* @__PURE__ */ jsx(ValidBadge, { valid: validRaw }) : /* @__PURE__ */ jsx(ProtocolValue, { value: valid }),
      when: valid != null
    },
    { label: WORMHOLE_VM_FIELD_LABELS.reason, value: /* @__PURE__ */ jsx(ProtocolValue, { value: reason }), when: text2(rawOf(reason)).trim().length > 0 },
    { label: WORMHOLE_VM_FIELD_LABELS.emitterChain, value: /* @__PURE__ */ jsx(ProtocolValue, { value: emitterChain }), when: emitterChain != null },
    { label: WORMHOLE_VM_FIELD_LABELS.emitterAddress, value: /* @__PURE__ */ jsx(ProtocolValue, { value: emitterAddress }), when: emitterAddress != null },
    { label: WORMHOLE_VM_FIELD_LABELS.sequence, value: /* @__PURE__ */ jsx(ProtocolValue, { value: sequence }), when: sequence != null },
    {
      label: WORMHOLE_FIELD_LABELS.vaaId,
      value: vaaId ? /* @__PURE__ */ jsx(IdentifierFact, { protocolId: WORMHOLE.protocolId, fact: WORMHOLE_VAA_ID_FACT, value: vaaId }) : null,
      when: vaaId
    },
    { label: WORMHOLE_VM_FIELD_LABELS.version, value: /* @__PURE__ */ jsx(ProtocolValue, { value: version }), when: version != null },
    { label: WORMHOLE_VM_FIELD_LABELS.timestamp, value: /* @__PURE__ */ jsx(ProtocolValue, { value: timestamp }), when: timestamp != null },
    { label: WORMHOLE_VM_FIELD_LABELS.nonce, value: /* @__PURE__ */ jsx(ProtocolValue, { value: nonce }), when: nonce != null },
    { label: WORMHOLE_VM_FIELD_LABELS.consistencyLevel, value: /* @__PURE__ */ jsx(ProtocolValue, { value: consistency }), when: consistency != null },
    { label: WORMHOLE_VM_FIELD_LABELS.guardianSet, value: /* @__PURE__ */ jsx(ProtocolValue, { value: guardianSet }), when: guardianSet != null },
    {
      label: WORMHOLE_VM_FIELD_LABELS.signatures,
      value: `${sigList.length} signature${sigList.length === 1 ? "" : "s"}`,
      when: sigList.length > 0
    },
    { label: WORMHOLE_VM_FIELD_LABELS.hash, value: /* @__PURE__ */ jsx(Hex, { value: rawOf(hash) }), when: hash != null }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: WORMHOLE.protocolId, icon: ShieldCheck }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: call.label ?? "parseAndVerifyVM" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: WORMHOLE.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: WORMHOLE.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        payloadDecoded ? /* @__PURE__ */ jsxs("div", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("div", { className: "mb-1 text-xs font-medium text-muted-foreground", children: WORMHOLE_VM_FIELD_LABELS.payload }),
          /* @__PURE__ */ jsx(ProtocolValue, { value: payload })
        ] }) : hasHex(payloadHex) && /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: WORMHOLE_VM_FIELD_LABELS.payload }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(Hex, { value: payloadHex }) })
        ] }),
        sigList.length > 0 && /* @__PURE__ */ jsx(VmSignatures, { signatures: sigList }),
        encodedVm != null && (encodedDecoded ? /* @__PURE__ */ jsxs("div", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("div", { className: "mb-1 text-xs font-medium text-muted-foreground", children: WORMHOLE_VM_FIELD_LABELS.encodedVaa }),
          /* @__PURE__ */ jsx(ProtocolValue, { value: encodedVm })
        ] }) : hasHex(encodedHex) && /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: `${WORMHOLE_VM_FIELD_LABELS.encodedVaa} (raw)` }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(Hex, { value: encodedHex }) })
        ] })),
        /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: "All call arguments" }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: call.params, emptyLabel: "No decoded arguments" }) })
        ] })
      ]
    }
  );
}
function stepFacts(step) {
  return [
    { label: WORMHOLE_EXECUTOR_FIELD_LABELS.gasLimit, value: text2(step.gasLimit), when: step.gasLimit != null },
    { label: WORMHOLE_EXECUTOR_FIELD_LABELS.msgValue, value: text2(step.msgValue), when: step.msgValue != null },
    { label: WORMHOLE_EXECUTOR_FIELD_LABELS.amount, value: text2(step.amount), when: step.amount != null },
    {
      label: WORMHOLE_EXECUTOR_FIELD_LABELS.recipient,
      value: /* @__PURE__ */ jsx(WormholeActor, { value: step.recipient }),
      when: text2(displayOf(step.recipient)) !== ""
    }
  ];
}
function WormholeRelayInstructions({
  decoded,
  display
}) {
  const d = isRecord2(decoded) ? decoded : {};
  const instructions = Array.isArray(d.instructions) ? d.instructions : [];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: WORMHOLE.protocolId, icon: ListChecks }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: text2(display) || "Relay instructions" })
  ] });
  return /* @__PURE__ */ jsx(
    SectionCard,
    {
      title,
      protocolId: WORMHOLE.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: WORMHOLE.protocolId }),
      children: instructions.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No decoded instructions" }) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-3", children: instructions.map((step, i) => {
        const s = isRecord2(step) ? step : {};
        return /* @__PURE__ */ jsxs("li", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx("div", { className: "mb-1 inline-flex items-center rounded-sm bg-muted px-1.5 py-px text-2xs font-medium text-muted-foreground", children: text2(s.type) || WORMHOLE_EXECUTOR_FIELD_LABELS.type }),
          /* @__PURE__ */ jsx(FactGrid, { facts: stepFacts(s) })
        ] }, i);
      }) })
    }
  );
}
function Hex2({ value }) {
  const t = text2(displayOf(value));
  if (!t) return null;
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 items-center gap-1 align-middle", children: [
    /* @__PURE__ */ jsx(MiddleEllipsis, { value: t, kind: "hash" }),
    /* @__PURE__ */ jsx(CopyButton, { value: t })
  ] });
}
function chain(v) {
  return text2(displayOf(v));
}
function WormholeSignedQuote({
  decoded,
  display
}) {
  const d = isRecord2(decoded) ? decoded : {};
  const expiry = text2(d.expiryTimeIso) || text2(d.expiryTime);
  const facts = [
    { label: WORMHOLE_QUOTE_FIELD_LABELS.srcChain, value: chain(d.srcChain), when: d.srcChain != null },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.dstChain, value: chain(d.dstChain), when: d.dstChain != null },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.quoterAddress, value: /* @__PURE__ */ jsx(WormholeActor, { value: d.quoterAddress }), when: d.quoterAddress },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.payeeAddress, value: /* @__PURE__ */ jsx(WormholeActor, { value: d.payeeAddress }), when: d.payeeAddress },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.baseFee, value: text2(d.baseFee), when: d.baseFee != null },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.dstGasPrice, value: text2(d.dstGasPrice), when: d.dstGasPrice != null },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.srcPrice, value: text2(d.srcPrice), when: d.srcPrice != null },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.dstPrice, value: text2(d.dstPrice), when: d.dstPrice != null },
    { label: WORMHOLE_QUOTE_FIELD_LABELS.expiryTime, value: expiry, when: expiry }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: WORMHOLE.protocolId, icon: ScrollText }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: text2(display) || "Executor signed quote" }),
    d.prefix != null && /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-sm bg-primary/10 px-1.5 py-px text-2xs font-medium text-primary", children: text2(d.prefix) })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: WORMHOLE.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: WORMHOLE.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        d.signature != null && /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: WORMHOLE_QUOTE_FIELD_LABELS.signature }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(Hex2, { value: d.signature }) })
        ] })
      ]
    }
  );
}
function WormholeApplicationPayload({ value, chainId }) {
  if (value == null || value === "0x") return null;
  return /* @__PURE__ */ jsxs("section", { className: "mt-3 min-w-0", "aria-label": WORMHOLE_FIELD_LABELS.applicationPayload, children: [
    /* @__PURE__ */ jsx("h4", { className: "mb-2 text-xs font-medium text-muted-foreground", children: WORMHOLE_FIELD_LABELS.applicationPayload }),
    /* @__PURE__ */ jsx(ProtocolValue, { value, chain: text2(chainId) || void 0 })
  ] });
}
function WormholeTransferPayload({
  decoded,
  display
}) {
  const d = isRecord2(decoded) ? decoded : {};
  const tokenChain = text2(d.token_chain_name) || text2(displayOf(d.tokenChain));
  const toChain = text2(d.to_chain_name) || text2(displayOf(d.toChain));
  const facts = [
    { label: WORMHOLE_FIELD_LABELS.amount, value: text2(d.amount), when: d.amount != null },
    { label: WORMHOLE_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(WormholeActor, { value: d.token_address ?? d.tokenAddress, chainId: d.token_chain_id }), when: d.token_address ?? d.tokenAddress },
    { label: WORMHOLE_FIELD_LABELS.tokenChain, value: tokenChain, when: tokenChain },
    { label: WORMHOLE_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(WormholeActor, { value: d.to_address ?? d.to, chainId: d.to_chain_id }), when: d.to_address ?? d.to },
    { label: WORMHOLE_FIELD_LABELS.recipientChain, value: toChain, when: toChain }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: WORMHOLE.protocolId, icon: ArrowLeftRight }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: text2(display) || "Token transfer payload" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: WORMHOLE.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: WORMHOLE.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsx(WormholeApplicationPayload, { value: d.custom_payload, chainId: d.to_chain_id })
      ]
    }
  );
}
function GuardianSignatures({
  signatures
}) {
  return /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
    /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: `${WORMHOLE_FIELD_LABELS.signatures} (${signatures.length})` }),
    /* @__PURE__ */ jsx("ul", { className: "mt-2 space-y-1", children: signatures.map((sig, i) => {
      const idx = sig.index != null ? text2(sig.index) : String(i);
      return /* @__PURE__ */ jsxs(
        "li",
        {
          className: "flex min-w-0 items-center gap-2 text-2xs",
          children: [
            /* @__PURE__ */ jsxs("span", { className: "w-8 shrink-0 text-right tabular-nums text-muted-foreground", children: [
              "#",
              idx
            ] }),
            /* @__PURE__ */ jsx(Hex, { value: sig.signature })
          ]
        },
        `${idx}-${i}`
      );
    }) })
  ] });
}
function WormholeVaa({ decoded, display }) {
  const d = isRecord2(decoded) ? decoded : {};
  const body = isRecord2(d.body) ? d.body : void 0;
  const header = isRecord2(d.header) ? d.header : void 0;
  const appPayload = isEnriched(d.decoded_payload) ? d.decoded_payload : void 0;
  const payload = appPayload ? void 0 : isRecord2(d.decoded_payload) ? d.decoded_payload : "type" in d || "amount" in d ? d : void 0;
  const signatures = Array.isArray(d.signatures) ? d.signatures : [];
  const payloadHex = text2(d.payload_hex) || text2(payload?.raw);
  const facts = [
    // The canonical VAA identity (emitterChain/emitter/sequence) — the cross-chain
    // correlation key an analyst pastes into WormholeScan / a consuming protocol.
    { label: WORMHOLE_FIELD_LABELS.vaaId, value: /* @__PURE__ */ jsx(Hex, { value: body?.vaa_id }), when: body?.vaa_id != null },
    { label: WORMHOLE_FIELD_LABELS.emitterChain, value: text2(body?.emitter_chain_name), when: body?.emitter_chain_name != null },
    { label: WORMHOLE_FIELD_LABELS.sequence, value: text2(body?.sequence), when: body?.sequence != null },
    { label: WORMHOLE_FIELD_LABELS.emitterAddress, value: /* @__PURE__ */ jsx(WormholeActor, { value: body?.emitter_address, chainId: body?.emitter_chain_id }), when: body?.emitter_address != null },
    {
      label: WORMHOLE_FIELD_LABELS.payloadType,
      value: payload?.type != null ? /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-sm bg-primary/10 px-1.5 py-px text-2xs font-medium text-primary", children: text2(payload.type) }) : null,
      when: payload?.type != null
    },
    { label: WORMHOLE_FIELD_LABELS.amount, value: text2(payload?.amount), when: payload?.amount != null },
    { label: WORMHOLE_FIELD_LABELS.token, value: /* @__PURE__ */ jsx(WormholeActor, { value: payload?.token_address, chainId: payload?.token_chain_id }), when: payload?.token_address != null },
    { label: WORMHOLE_FIELD_LABELS.tokenChain, value: text2(payload?.token_chain_name), when: payload?.token_chain_name != null },
    { label: WORMHOLE_FIELD_LABELS.recipient, value: /* @__PURE__ */ jsx(WormholeActor, { value: payload?.to_address, chainId: payload?.to_chain_id }), when: payload?.to_address != null },
    { label: WORMHOLE_FIELD_LABELS.recipientChain, value: text2(payload?.to_chain_name), when: payload?.to_chain_name != null },
    { label: WORMHOLE_FIELD_LABELS.consistencyLevel, value: text2(body?.consistency_level), when: body?.consistency_level != null },
    {
      label: WORMHOLE_FIELD_LABELS.guardianSet,
      value: text2(header?.guardian_set_index),
      when: header?.guardian_set_index != null
    },
    {
      label: WORMHOLE_FIELD_LABELS.signatures,
      value: `${signatures.length} signature${signatures.length === 1 ? "" : "s"}`,
      when: signatures.length > 0
    }
  ];
  const title = /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-2", children: [
    /* @__PURE__ */ jsx(ProtocolIcon, { protocolId: WORMHOLE.protocolId, icon: Radio }),
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: text2(display) || "Wormhole VAA" })
  ] });
  return /* @__PURE__ */ jsxs(
    SectionCard,
    {
      title,
      protocolId: WORMHOLE.protocolId,
      meta: /* @__PURE__ */ jsx(ProtocolBadge, { protocolId: WORMHOLE.protocolId }),
      children: [
        /* @__PURE__ */ jsx(FactGrid, { facts }),
        /* @__PURE__ */ jsx(WormholeApplicationPayload, { value: payload?.custom_payload, chainId: payload?.to_chain_id }),
        appPayload && /* @__PURE__ */ jsx("div", { className: "mt-3 min-w-0", children: /* @__PURE__ */ jsx(ProtocolValue, { value: appPayload }) }),
        signatures.length > 0 && /* @__PURE__ */ jsx(GuardianSignatures, { signatures }),
        payloadHex && /* @__PURE__ */ jsxs("details", { className: "mt-3 min-w-0", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs text-muted-foreground hover:text-foreground", children: WORMHOLE_FIELD_LABELS.payloadHex }),
          /* @__PURE__ */ jsx("div", { className: "pt-2", children: /* @__PURE__ */ jsx(Hex, { value: payloadHex }) })
        ] })
      ]
    }
  );
}

// src/protocols/wormhole/index.ts
var PID = WORMHOLE.protocolId;
var WORMHOLE_HINTS = {
  valueHints: [
    { protocolId: PID, kind: WORMHOLE_VAA_KIND, component: WORMHOLE_COMPONENTS.vaa, fields: [] },
    {
      protocolId: PID,
      kind: WORMHOLE_SIGNED_QUOTE_KIND,
      component: WORMHOLE_COMPONENTS.signedQuote,
      fields: []
    },
    {
      protocolId: PID,
      kind: WORMHOLE_EXECUTOR_REQUEST_KIND,
      component: WORMHOLE_COMPONENTS.executorRequest,
      fields: []
    },
    {
      protocolId: PID,
      kind: WORMHOLE_RELAY_INSTRUCTIONS_KIND,
      component: WORMHOLE_COMPONENTS.relayInstructions,
      fields: []
    },
    {
      protocolId: PID,
      kind: WORMHOLE_TRANSFER_PAYLOAD_KIND,
      component: WORMHOLE_COMPONENTS.transferPayload,
      fields: []
    }
  ],
  eventHints: [],
  callHints: [
    // The Core Bridge VAA reads. SCOPED to `wormhole` so an unrelated contract's
    // same-named `parseVM` (or a fork's) isn't claimed; the card reads the parsed
    // `vm` struct off `returnParams`, so it no-ops gracefully on a body that
    // matched the name without the Core Bridge return ABI. `parseAndVerifyVM`
    // adds the `(valid, reason)` verification result; `parseVM` returns the
    // struct alone.
    { protocolId: PID, call: "parseAndVerifyVM", component: WORMHOLE_COMPONENTS.parseVm, summaryFields: [] },
    { protocolId: PID, call: "parseVM", component: WORMHOLE_COMPONENTS.parseVm, summaryFields: [] }
  ]
};
function registerWormhole(registry) {
  registry.registerValue(WORMHOLE_COMPONENTS.vaa, WormholeVaa);
  registry.registerValue(WORMHOLE_COMPONENTS.signedQuote, WormholeSignedQuote);
  registry.registerValue(WORMHOLE_COMPONENTS.executorRequest, WormholeExecutorRequest);
  registry.registerValue(WORMHOLE_COMPONENTS.relayInstructions, WormholeRelayInstructions);
  registry.registerValue(WORMHOLE_COMPONENTS.transferPayload, WormholeTransferPayload);
  registry.registerCall(WORMHOLE_COMPONENTS.parseVm, WormholeParseVM, { ownsReturns: true });
  registry.registerHints(WORMHOLE_HINTS);
  return registry;
}

export { WORMHOLE_HINTS, WormholeExecutorRequest, WormholeParseVM, WormholeRelayInstructions, WormholeSignedQuote, WormholeTransferPayload, WormholeVaa, registerWormhole };
//# sourceMappingURL=chunk-65ZEHA6A.js.map
//# sourceMappingURL=chunk-65ZEHA6A.js.map